import { TFunction } from "i18next";
import { DailyWeatherForecastData } from "@/types/forecast";

export const CHART_METRIC_IDS = [
  "temperature",
  "feelsLike",
  "windSpeed",
  "humidity",
  "uvIndex",
  "cloudCover",
] as const;

export type ChartMetricId = (typeof CHART_METRIC_IDS)[number];

export const METRIC_IDS = [
  ...CHART_METRIC_IDS,
  "airQuality",
  "precipitation",
] as const;

export type MetricId = (typeof METRIC_IDS)[number];

export type MetricSelection = MetricId | "overview";

export type Insight = {
  key: string;
  label: string;
  value: string;
  caption?: string;
};

export type MetricDef = {
  id: MetricId;
  labelKey: string;
  shortLabelKey?: string;
  unitKey?: string;
  unitText?: string;
  icon?: string;
  getHourly?: (day: DailyWeatherForecastData) => number[];
  chart?: {
    tickInterval: number;
    formatShort: (value: number, t: TFunction) => string;
    overlay?: { field: string; labelKey: string; minDelta?: number };
  };
  scale?: { min: number; max: number };
  describe?: (value: number, t: TFunction) => string;
};

const toNumberArray = (raw: unknown): number[] =>
  Array.isArray(raw)
    ? raw
        .map((v) => (typeof v === "number" ? v : Number(v)))
        .filter(Number.isFinite)
    : [];

const formatSignedDegrees = (value: number): string =>
  value > 0 ? `+${Math.floor(value)}°` : `${Math.floor(value)}°`;

export const METRICS_REGISTRY: Record<MetricId, MetricDef> = {
  temperature: {
    id: "temperature",
    labelKey: "metrics.temperature",
    unitText: "°C",
    getHourly: (day) => toNumberArray(day.temperature),
    chart: {
      tickInterval: 5,
      formatShort: (v) => formatSignedDegrees(v),
      overlay: {
        field: "feelsLike",
        labelKey: "metrics.feels_like",
        minDelta: 2,
      },
    },
  },

  feelsLike: {
    id: "feelsLike",
    labelKey: "metrics.feels_like",
    unitText: "°C",
    getHourly: (day) => toNumberArray(day.feelsLike),
    chart: {
      tickInterval: 5,
      formatShort: (v) => formatSignedDegrees(v),
    },
  },

  windSpeed: {
    id: "windSpeed",
    labelKey: "metrics.wind_speed",
    shortLabelKey: "metrics.wind",
    unitKey: "measures.km",
    icon: "/shared/wind.png",
    getHourly: (day) => toNumberArray(day.windSpeed),
    chart: {
      tickInterval: 1,
      formatShort: (v, t) => `${Math.floor(v)} ${t("measures.km")}`,
      overlay: { field: "windGusts", labelKey: "insights.gusts", minDelta: 5 },
    },
  },

  humidity: {
    id: "humidity",
    labelKey: "metrics.humidity",
    unitText: "%",
    icon: "/shared/humidity.png",
    getHourly: (day) => toNumberArray(day.humidity),
    chart: {
      tickInterval: 10,
      formatShort: (v) => `${Math.floor(v)}%`,
    },
    describe: (humidity, t) => {
      if (humidity <= 30) return t("day_highlight.descriptions.humidity.dry");
      if (humidity <= 50)
        return t("day_highlight.descriptions.humidity.moderate");
      if (humidity <= 70)
        return t("day_highlight.descriptions.humidity.noticeable");
      if (humidity <= 85) return t("day_highlight.descriptions.humidity.high");
      return t("day_highlight.descriptions.humidity.very_high");
    },
  },

  uvIndex: {
    id: "uvIndex",
    labelKey: "metrics.uv_index",
    icon: "/shared/uv_index.png",
    getHourly: (day) => toNumberArray(day.uvIndex),
    chart: {
      tickInterval: 1,
      formatShort: (v) => `${Math.floor(v)}`,
    },
    scale: { min: 0, max: 12 },
    describe: (uv, t) => {
      if (uv <= 2) return t("day_highlight.descriptions.uv_index.safe");
      if (uv <= 5)
        return t("day_highlight.descriptions.uv_index.protection_desirable");
      if (uv <= 7)
        return t("day_highlight.descriptions.uv_index.protection_required");
      if (uv <= 10) return t("day_highlight.descriptions.uv_index.avoid_sun");
      return t("day_highlight.descriptions.uv_index.extreme");
    },
  },

  cloudCover: {
    id: "cloudCover",
    labelKey: "metrics.cloudiness",
    unitText: "%",
    icon: "/shared/cloud_cover.png",
    getHourly: (day) => toNumberArray(day.cloudCover),
    chart: {
      tickInterval: 10,
      formatShort: (v) => `${Math.floor(v)}%`,
    },
    describe: (cloudCover, t) => {
      if (cloudCover <= 25)
        return t("day_highlight.descriptions.cloud_cover.clear");
      if (cloudCover <= 40)
        return t("day_highlight.descriptions.cloud_cover.partly_cloudy");
      if (cloudCover <= 60)
        return t("day_highlight.descriptions.cloud_cover.mostly_cloudy");
      if (cloudCover <= 75)
        return t("day_highlight.descriptions.cloud_cover.overcast");
      return t("day_highlight.descriptions.cloud_cover.fully_cloudy");
    },
  },

  airQuality: {
    id: "airQuality",
    labelKey: "metrics.air_quality",
    icon: "/shared/air_quality.png",
    getHourly: (day) => toNumberArray(day.airQuality),
    scale: { min: 0, max: 500 },
    describe: (aqi, t) => {
      if (aqi <= 50) return t("day_highlight.descriptions.air_quality.good");
      if (aqi <= 100)
        return t("day_highlight.descriptions.air_quality.moderate");
      if (aqi <= 150)
        return t("day_highlight.descriptions.air_quality.unhealthy_sensitive");
      if (aqi <= 200)
        return t("day_highlight.descriptions.air_quality.unhealthy");
      if (aqi <= 300)
        return t("day_highlight.descriptions.air_quality.very_unhealthy");
      return t("day_highlight.descriptions.air_quality.hazardous");
    },
  },

  precipitation: {
    id: "precipitation",
    labelKey: "metrics.precipitation",
    unitText: "%",
    icon: "/shared/precipitation.png",
  },
};

export const getMetricLabel = (
  id: MetricId,
  t: TFunction,
  opts?: { short?: boolean },
): string => {
  const def = METRICS_REGISTRY[id];
  const key = opts?.short ? (def.shortLabelKey ?? def.labelKey) : def.labelKey;
  return t(key);
};

export const getMetricUnitText = (
  id: MetricId,
  t: TFunction,
): string | null => {
  const def = METRICS_REGISTRY[id];
  if (def.unitKey) return t(def.unitKey);
  return def.unitText ?? null;
};

export const formatChartValue = (
  id: MetricId,
  value: number,
  t: TFunction,
): string => {
  const def = METRICS_REGISTRY[id];
  return def.chart ? def.chart.formatShort(value, t) : `${Math.floor(value)}`;
};

export const getMetricScalePercent = (id: MetricId, value: number): number => {
  const scale = METRICS_REGISTRY[id].scale;
  if (!scale) return 0;
  const pct = ((value - scale.min) / (scale.max - scale.min)) * 100;
  return Math.max(0, Math.min(100, pct));
};

export const buildChartMetricOptions = (
  t: TFunction,
): Record<
  ChartMetricId,
  { label: string; value: ChartMetricId; tickInterval: number }
> => {
  const entries = CHART_METRIC_IDS.map((id) => {
    const def = METRICS_REGISTRY[id];
    return [
      id,
      {
        label: t(def.labelKey),
        value: id,
        tickInterval: def.chart!.tickInterval,
      },
    ];
  });
  return Object.fromEntries(entries);
};

type InsightCtx = { t: TFunction; hourIndex: number; isToday: boolean };

const fmtHour = (i: number) => `${String(i).padStart(2, "0")}:00`;

const at = (raw: number[] | undefined, idx: number): number | null => {
  if (!Array.isArray(raw) || !raw.length) return null;
  const v = Number(raw[Math.min(idx, raw.length - 1)]);
  return Number.isFinite(v) ? v : null;
};

const seriesStats = (values: number[]) => {
  if (!values.length) return null;
  let maxIdx = 0;
  let minIdx = 0;
  let sum = 0;
  values.forEach((v, i) => {
    if (v > values[maxIdx]) maxIdx = i;
    if (v < values[minIdx]) minIdx = i;
    sum += v;
  });
  return {
    max: values[maxIdx],
    maxIdx,
    min: values[minIdx],
    minIdx,
    avg: sum / values.length,
  };
};

export const getMetricDelta = (
  id: MetricId,
  day: DailyWeatherForecastData,
): number | null => {
  const delta = day.vsYesterday?.[id as keyof typeof day.vsYesterday];
  if (typeof delta !== "number" || !Number.isFinite(delta)) return null;
  return Math.round(delta) === 0 ? null : delta;
};

export const formatMetricDeltaAbs = (
  id: MetricId,
  v: number,
  t: TFunction,
): string => {
  const rounded = Math.abs(Math.round(v));
  switch (id) {
    case "temperature":
    case "feelsLike":
      return `${rounded}°`;
    case "windSpeed":
      return `${rounded} ${t("measures.km")}`;
    case "humidity":
    case "cloudCover":
      return `${rounded}%`;
    default:
      return `${rounded}`;
  }
};

const POLLEN_SPECIES: Array<{
  key: keyof NonNullable<DailyWeatherForecastData["pollen"]>;
  labelKey: string;
}> = [
  { key: "alder", labelKey: "insights.pollen_alder" },
  { key: "birch", labelKey: "insights.pollen_birch" },
  { key: "grass", labelKey: "insights.pollen_grass" },
  { key: "mugwort", labelKey: "insights.pollen_mugwort" },
  { key: "ragweed", labelKey: "insights.pollen_ragweed" },
];

export const buildMetricInsights = (
  id: MetricId,
  day: DailyWeatherForecastData,
  { t, hourIndex, isToday }: InsightCtx,
): Insight[] => {
  const def = METRICS_REGISTRY[id];
  const atTime = (i: number) => t("insights.at_time", { time: fmtHour(i) });
  const nowCaption = isToday ? t("insights.now") : atTime(hourIndex);
  const fmt = (v: number) => formatChartValue(id, v, t);

  if (id === "precipitation") {
    return [
      {
        key: "probability",
        label: t("metrics.precipitation"),
        value: `${Math.floor(day.precipitation ?? 0)}%`,
      },
      {
        key: "sum",
        label: t("metrics.precipitation_sum"),
        value: `${Math.floor(day.precipitationSum ?? 0)} ${t("measures.mm")}`,
      },
      {
        key: "hours",
        label: t("metrics.precipitation_hours"),
        value: `${Math.floor(day.precipitationHours ?? 0)} ${t(
          "metrics.precipitation_hours_metric",
        )}.`,
      },
    ];
  }

  const values = def.getHourly ? def.getHourly(day) : [];
  const stats = seriesStats(values);
  if (!stats) return [];

  const current = at(values, hourIndex) ?? stats.avg;
  const insights: Insight[] = [];
  const push = (item: Insight | null) => {
    if (item) insights.push(item);
  };

  const maxInsight: Insight = {
    key: "max",
    label: t("insights.day_max"),
    value: fmt(stats.max),
    caption: atTime(stats.maxIdx),
  };
  const minInsight: Insight = {
    key: "min",
    label: t("insights.day_min"),
    value: fmt(stats.min),
    caption: atTime(stats.minIdx),
  };
  const avgInsight: Insight = {
    key: "avg",
    label: t("insights.day_avg"),
    value: fmt(stats.avg),
  };

  switch (id) {
    case "temperature": {
      push(maxInsight);
      push(minInsight);
      push(avgInsight);
      const feelsNow = at(day.feelsLike, hourIndex);
      if (feelsNow !== null) {
        push({
          key: "feels",
          label: t("metrics.feels_like"),
          value: formatSignedDegrees(feelsNow),
          caption: nowCaption,
        });
      }
      push({
        key: "range",
        label: t("insights.range"),
        value: `${Math.floor(stats.max - stats.min)}°`,
      });
      break;
    }

    case "feelsLike": {
      push(maxInsight);
      push(minInsight);
      push(avgInsight);
      const actualNow = at(day.temperature, hourIndex);
      if (actualNow !== null) {
        push({
          key: "diff",
          label: t("insights.diff_actual"),
          value: formatSignedDegrees(current - actualNow),
          caption: nowCaption,
        });
      }
      break;
    }

    case "windSpeed": {
      push(maxInsight);
      const gusts = toNumberArray(day.windGusts);
      const gustsStats = seriesStats(gusts);
      if (gustsStats) {
        push({
          key: "gusts",
          label: t("insights.gusts"),
          value: `${Math.floor(gustsStats.max)} ${t("measures.km")}`,
          caption: atTime(gustsStats.maxIdx),
        });
      }
      push(avgInsight);
      const directionNow = at(day.windDirection, hourIndex);
      if (directionNow !== null) {
        push({
          key: "direction",
          label: t("insights.wind_direction"),
          value: `${Math.floor(directionNow)}°`,
          caption: nowCaption,
        });
      }
      break;
    }

    case "humidity": {
      push(maxInsight);
      const dewNow = at(day.dewPoint, hourIndex);
      if (dewNow !== null) {
        push({
          key: "dew",
          label: t("insights.dew_point"),
          value: formatSignedDegrees(dewNow),
          caption: nowCaption,
        });
      }
      const visibilityNow = at(day.visibility, hourIndex);
      if (visibilityNow !== null) {
        push({
          key: "visibility",
          label: t("insights.visibility"),
          value:
            visibilityNow >= 1000
              ? `${Math.round(visibilityNow / 1000)} ${t("measures.km_plain")}`
              : `${Math.round(visibilityNow)} ${t("measures.m")}`,
          caption: nowCaption,
        });
      }
      push(avgInsight);
      break;
    }

    case "uvIndex": {
      push(maxInsight);
      const cautionHours = values
        .map((v, i) => (v >= 3 ? i : -1))
        .filter((i) => i >= 0);
      push({
        key: "caution",
        label: t("insights.uv_caution"),
        value: cautionHours.length
          ? `${fmtHour(cautionHours[0])}–${fmtHour(
              cautionHours[cautionHours.length - 1],
            )}`
          : "—",
        caption: cautionHours.length
          ? def.describe?.(stats.max, t)
          : t("insights.uv_no_caution"),
      });
      break;
    }

    case "cloudCover": {
      const low = at(day.cloudCoverLow, hourIndex);
      const mid = at(day.cloudCoverMid, hourIndex);
      const high = at(day.cloudCoverHigh, hourIndex);
      if (low !== null)
        push({
          key: "low",
          label: t("insights.cloud_low"),
          value: `${Math.floor(low)}%`,
          caption: nowCaption,
        });
      if (mid !== null)
        push({
          key: "mid",
          label: t("insights.cloud_mid"),
          value: `${Math.floor(mid)}%`,
          caption: nowCaption,
        });
      if (high !== null)
        push({
          key: "high",
          label: t("insights.cloud_high"),
          value: `${Math.floor(high)}%`,
          caption: nowCaption,
        });
      const sun = day.sunshineDuration;
      const daylight = day.daylightDuration;
      if (
        typeof sun === "number" &&
        typeof daylight === "number" &&
        Number.isFinite(sun) &&
        Number.isFinite(daylight) &&
        daylight > 0
      ) {
        push({
          key: "sunshine",
          label: t("insights.sunshine"),
          value: t("insights.sunshine_value", {
            sun: Math.round(sun / 3600),
            daylight: Math.round(daylight / 3600),
          }),
        });
      }
      break;
    }

    case "airQuality": {
      const pollutants: Array<{ key: string; label: string; raw?: number[] }> =
        [
          { key: "pm25", label: "PM2.5", raw: day.pm25 },
          { key: "pm10", label: "PM10", raw: day.pm10 },
          { key: "ozone", label: "O₃", raw: day.ozone },
          { key: "no2", label: "NO₂", raw: day.no2 },
        ];
      pollutants.forEach(({ key, label, raw }) => {
        const v = at(raw, hourIndex);
        if (v !== null) {
          push({
            key,
            label,
            value: `${Math.round(v)} ${t("measures.ugm3")}`,
            caption: nowCaption,
          });
        }
      });
      let dominant: { labelKey: string; value: number } | null = null;
      POLLEN_SPECIES.forEach(({ key, labelKey }) => {
        const v = at(day.pollen?.[key], hourIndex);
        if (v !== null && v > 0 && (!dominant || v > dominant.value)) {
          dominant = { labelKey, value: v };
        }
      });
      if (dominant) {
        push({
          key: "pollen",
          label: t("insights.pollen"),
          value: `${Math.round((dominant as { value: number }).value)} ${t(
            "measures.grains",
          )}`,
          caption: t((dominant as { labelKey: string }).labelKey),
        });
      }
      break;
    }
  }

  return insights;
};

export const getAirDescription = (aqi: number, t: TFunction): string =>
  METRICS_REGISTRY.airQuality.describe!(aqi, t);

export const getCloudCoverDescription = (
  cloudCover: number,
  t: TFunction,
): string => METRICS_REGISTRY.cloudCover.describe!(cloudCover, t);

export const getHumidityDescription = (
  humidity: number,
  t: TFunction,
): string => METRICS_REGISTRY.humidity.describe!(humidity, t);

export const getUvIndexDescription = (uv: number, t: TFunction): string =>
  METRICS_REGISTRY.uvIndex.describe!(uv, t);
